import React, { useEffect, useState } from 'react';
import { useTranslation } from "react-i18next";

function Navbar() {
    const [theme, setTheme] = useState(localStorage.getItem("theme") || (window.matchMedia('(prefers-color-scheme: dark)').matches ? "dark" : "light"));
    const [isScrolled, setIsScrolled] = useState(false);
    const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
    const { i18n, t } = useTranslation();

    // Theme handling
    useEffect(() => {
        localStorage.setItem("theme", theme);
        document.querySelector("html").setAttribute("data-theme", theme);
    }, [theme]);

    const handleThemeChange = () => {
        setTheme(theme === "light" ? "dark" : "light");
    };

    // Scroll handling
    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };
        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    const handleDownload = () => {
        const link = document.createElement('a');
        link.href = '/Ali Ameur.pdf';
        link.download = 'Ali Ameur.pdf';
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const handleLanguageChange = () => {
        i18n.changeLanguage(i18n.language === 'fr' ? 'en' : 'fr');
    };

    const navLinks = [
        { name: t('navbar.home'), href: "#home" },
        { name: t('navbar.about'), href: "#about" },
        { name: t('navbar.experience'), href: "#experience" },
        { name: t('navbar.projects'), href: "#projects" },
        { name: t('navbar.contact'), href: "#contact" },
    ];

    return (
        <header className={`fixed inset-x-0 top-0 z-50 backdrop-blur-md transition-all duration-300 ${isScrolled ? 'bg-base-200/90 shadow-sm' : 'bg-base-200/50'}`}>
            {/* Gradient accent bar */}
            <div className="absolute inset-x-0 top-0 h-0.5 bg-gradient-to-r from-primary to-secondary"></div>

            <nav className="container mx-auto px-4 md:px-12">
                <div className="flex items-center justify-between h-16 md:h-20">
                    {/* Logo / Name */}
                    <div className="flex items-center flex-shrink-0">
                        <a href="#home" className="flex items-center space-x-2 group">
                            <svg
                                xmlns="http://www.w3.org/2000/svg"
                                viewBox="0 0 405 492"
                                className="w-10 h-10 md:w-12 md:h-12 fill-primary group-hover:fill-secondary transition-colors duration-300"
                                preserveAspectRatio="xMidYMid meet"
                            >
                                <g transform="translate(0,492) scale(0.1,-0.1)" stroke="none">
                                    <path d="M2009 3538 l-65 -114 313 -535 c173 -293 373 -633 446 -754 l132 -220 129 -3 c122 -2 128 -2 124 16 -6 23 -947 1627 -999 1702 -15 22 -16 21 -80 -92z" />
                                    <path d="M1493 3363 c-72 -120 -235 -396 -363 -613 -128 -217 -298 -504 -377 -637 -79 -133 -146 -249 -149 -257 -5 -14 10 -16 127 -16 l134 1 292 497 c160 273 329 561 375 640 80 138 83 142 98 121 8 -13 146 -246 306 -518 l291 -496 -239 -5 -240 -5 -64 -110 c-35 -60 -64 -113 -64 -117 0 -5 170 -8 379 -8 l378 0 124 -215 124 -214 265 -1 c146 -1 320 -3 388 -6 67 -3 122 -2 122 2 0 4 -30 58 -66 120 l-67 114 -251 0 -252 0 -18 33 c-33 57 -148 253 -368 627 -118 201 -331 563 -473 805 -142 242 -263 448 -270 458 -11 16 -28 -8 -142 -200z" />
                                    <path d="M1501 2522 c-62 -108 -203 -350 -314 -538 l-200 -341 -119 -6 c-66 -3 -179 -3 -251 0 -154 7 -133 19 -219 -126 -56 -93 -57 -96 -35 -103 12 -3 185 -6 384 -7 l362 -1 19 28 c22 32 317 529 424 717 l75 130 117 3 c104 2 117 5 112 19 -9 23 -212 385 -228 405 -11 15 -27 -7 -127 -180z" />
                                </g>
                            </svg>
                            <span className="text-xl md:text-2xl font-bold text-base-content group-hover:text-primary transition-colors duration-300">
                                Ali Ameur
                            </span>
                        </a>
                    </div>

                    {/* Desktop Navigation */}
                    <div className="hidden md:flex items-center space-x-1">
                        {navLinks.map((item) => (
                            <a
                                key={item.name}
                                href={item.href}
                                className="px-3 py-2 rounded-lg font-medium text-base-content hover:text-primary transition-colors duration-200"
                            >
                                {item.name}
                            </a>
                        ))}
                    </div>

                    {/* Right side controls */}
                    <div className="hidden md:flex items-center space-x-4">
                        {/* Language toggle */}
                        <button
                            onClick={handleLanguageChange}
                            className="px-3 py-1 rounded-md font-medium text-base-content hover:bg-base-200 transition-colors duration-200"
                        >
                            {i18n.language === 'fr' ? 'EN' : 'FR'}
                        </button>

                        {/* Theme toggle */}
                        <button
                            onClick={handleThemeChange}
                            className="p-2 rounded-full hover:bg-base-200 transition-colors duration-200"
                            aria-label="Toggle theme"
                        >
                            {theme === 'light' ? (
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                </svg>
                            ) : (
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                            )}
                        </button>

                        {/* Download CV button */}
                        <button
                            onClick={handleDownload}
                            className="btn btn-primary px-4 hover:-translate-y-0.5 transition-transform duration-200 flex items-center gap-1"
                        >
                            <span>{t('navbar.cv')}</span>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                        </button>
                    </div>

                    {/* Mobile menu button */}
                    <div className="flex md:hidden items-center space-x-4">
                        {/* Language toggle */}
                        <button
                            onClick={handleLanguageChange}
                            className="px-2 py-1 rounded-md text-sm font-medium"
                        >
                            {i18n.language === 'fr' ? 'EN' : 'FR'}
                        </button>

                        {/* Theme toggle */}
                        <button
                            onClick={handleThemeChange}
                            className="p-2 rounded-full"
                            aria-label="Toggle theme"
                        >
                            {theme === 'light' ? (
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M20.354 15.354A9 9 0 018.646 3.646 9.003 9.003 0 0012 21a9.003 9.003 0 008.354-5.646z" />
                                </svg>
                            ) : (
                                <svg className="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M12 3v1m0 16v1m9-9h-1M4 12H3m15.364 6.364l-.707-.707M6.343 6.343l-.707-.707m12.728 0l-.707.707M6.343 17.657l-.707.707M16 12a4 4 0 11-8 0 4 4 0 018 0z" />
                                </svg>
                            )}
                        </button>

                        {/* Mobile menu toggle */}
                        <button
                            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
                            className="p-2 rounded-md hover:bg-base-200 transition-colors duration-200"
                            aria-label="Toggle menu"
                        >
                            {mobileMenuOpen ? (
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M6 18L18 6M6 6l12 12" />
                                </svg>
                            ) : (
                                <svg className="w-6 h-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 6h16M4 12h16M4 18h16" />
                                </svg>
                            )}
                        </button>
                    </div>
                </div>
            </nav>

            {/* Mobile menu */}
            <div className={`md:hidden transition-all duration-300 ease-in-out overflow-hidden ${mobileMenuOpen ? 'max-h-96' : 'max-h-0'}`}>
                <div className="container mx-auto px-4 pb-4">
                    <div className="flex flex-col space-y-2 pt-2 border-t border-base-300">
                        {navLinks.map((item) => (
                            <a
                                key={item.name}
                                href={item.href}
                                onClick={(e) => setMobileMenuOpen(false)}
                                className="px-3 py-2 rounded-lg font-medium text-base-content hover:text-primary hover:bg-base-200/50 transition-colors duration-200"
                            >
                                {item.name}
                            </a>
                        ))}
                        <button
                            onClick={() => {
                                handleDownload();
                                setMobileMenuOpen(false);
                            }}
                            className="btn btn-primary px-4 py-2 mt-2 flex items-center justify-center gap-1"
                        >
                            <span>{t('navbar.cv')}</span>
                            <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth="2" d="M4 16v1a3 3 0 003 3h10a3 3 0 003-3v-1m-4-4l-4 4m0 0l-4-4m4 4V4" />
                            </svg>
                        </button>
                    </div>
                </div>
            </div>
        </header>
    );
}

export default Navbar;